import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Download, Loader2 } from "lucide-react";
import { exportTtdPriceReportCSV, getTtdPriceReport } from "@/lib/ttd-price-report.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StaffAccessNotice, isMfaError, staffErrorMessage } from "@/components/admin/StaffAccessNotice";

export const Route = createFileRoute("/_authenticated/admin/reporte-precios-ttd")({
  head: () => ({
    meta: [
      { title: "Reporte de precios TTD · Administración" },
      { name: "description", content: "Reporte de solo lectura de precios, incluidos y no incluidos de tours propios en Bókun." },
      { property: "og:title", content: "Reporte de precios TTD · Administración" },
      { property: "og:description", content: "Prepara precios todo incluido para Google Things To Do." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: Page,
});

function Page() {
  const load = useServerFn(getTtdPriceReport);
  const exportCsv = useServerFn(exportTtdPriceReportCSV);
  const { data, isLoading, error } = useQuery({ queryKey: ["ttd-price-report"], queryFn: () => load(), staleTime: 5 * 60_000, retry: false });

  const exp = useMutation({
    mutationFn: () => exportCsv(),
    onSuccess: (r) => {
      const url = URL.createObjectURL(new Blob([r.csv], { type: "text/csv;charset=utf-8" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte-precios-ttd-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`CSV exportado (${r.products} productos)`);
    },
    onError: (e) => toast.error(staffErrorMessage(e)),
  });

  return (
    <div className="space-y-6 p-4 md:p-8">
      <div>
        <h1 className="text-2xl font-bold">Reporte de precios para Google Things To Do</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Solo lectura. Muestra los precios actuales en Bókun y lo incluido / no incluido para que puedas sumar a mano los cargos obligatorios. No cambia nada.
        </p>
      </div>
      {isLoading && <p className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Leyendo productos de Bókun… puede tardar un minuto.</p>}
      {error && (isMfaError(error) ? <StaffAccessNotice error={error} /> : <p className="text-sm text-destructive">{staffErrorMessage(error)}</p>)}
      {data && (
        <>
          <div className="flex flex-wrap items-center gap-4">
            <p className="text-sm"><strong>{data.matched}</strong> productos de Mostrando México · <strong>{data.excludedOtherVendor}</strong> excluidos por tener otro proveedor (de {data.total})</p>
            <Button onClick={() => exp.mutate()} disabled={exp.isPending}>
              {exp.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Download className="mr-2 h-4 w-4" />}
              Exportar CSV
            </Button>
          </div>
          {data.partial && <p className="text-sm text-destructive">Bókun no entregó el catálogo completo; el reporte puede estar incompleto. Recarga más tarde.</p>}
          <div className="grid gap-4">
            {data.products.map((p) => (
              <Card key={p.id}>
                <CardHeader><CardTitle className="text-base">{p.title} <span className="font-normal text-muted-foreground">· Bókun {p.id}</span></CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  {p.error && <p className="text-destructive">No se pudo leer: {p.error}</p>}
                  {p.prices.length ? (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left">
                        <thead className="text-muted-foreground"><tr><th className="pr-3">Categoría</th><th className="pr-3">Tarifa</th><th className="pr-3">Temporada</th><th className="pr-3 text-right">Precio</th></tr></thead>
                        <tbody>
                          {p.prices.map((r, i) => (
                            <tr key={i} className="border-t border-border">
                              <td className="pr-3">{r.category ?? r.ticketCategory ?? "—"}</td>
                              <td className="pr-3">{r.rateTitle ?? "—"}</td>
                              <td className="pr-3">{r.rangeFrom || r.rangeTo ? `${r.rangeFrom ?? "…"} → ${r.rangeTo ?? "…"}` : "Siempre"}</td>
                              <td className="pr-3 text-right">{r.price ?? "—"} {r.currency ?? ""}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : !p.error && <p className="text-muted-foreground">Sin lista de precios en Bókun.</p>}
                  <List label="No incluido" items={p.excluded} />
                  <List label="Incluido" items={p.included} />
                  <List label="Extras" items={p.extras.map((e) => `${e.title}${e.price != null ? ` — ${e.price} ${e.currency ?? ""}` : ""}`)} />
                </CardContent>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function List({ label, items }: { label: string; items: string[] }) {
  return (
    <div>
      <p className="font-medium">{label}</p>
      {items.length ? <ul className="list-disc pl-5 whitespace-pre-line">{items.map((t, i) => <li key={i}>{t}</li>)}</ul> : <p className="text-muted-foreground">—</p>}
    </div>
  );
}
